import React, {useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {RECENTLY_USED_FILTERS_LIMIT} from 'components/Filters/constants'
import {filterMessages} from 'components/Filters/filterMessages'
import {getAllFilterKeys} from 'components/Filters/utils'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {useRecentlyUsedFilters} from 'hooks/useRecentlyUsedFilters'
import {getFiltersVisibility} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {getNeighborhoods, getPropertyBrands} from 'modules/sapiSearch/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {BottomSheet} from '@vio/atlas/src/components/designSystem/BottomSheet'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {ButtonLink} from '@vio/atlas/src/components/designSystem/ButtonLink'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {AllFiltersContent} from '../AllFiltersContent'

const allFilterKeys = getAllFilterKeys()
const elementName = 'allFilters'

export const AllFiltersBottomSheet = () => {
  const {formatMessage} = useIntl()
  const dispatch = useDispatch()
  const {
    getAppliedFiltersCount,
    getAppliedStarsLabel,
    getFilters,
    getAppliedFilters,
    toggleFilters,
    toggleMultiFilter,
    setMultiFilters,
    removeMultiFilters,
    getFiltersCount,
    submitFilters,
    resetFilters,
    clearFilters,
    calculateIfFiltersChanged
  } = useFiltersForm(allFilterKeys, elementName)

  const isOpen = useSelector(getFiltersVisibility)

  const neighborhoods = useSelector(getNeighborhoods)
  const propertyBrands = useSelector(getPropertyBrands)

  const filtersCount = getFiltersCount()

  const {getMappedAllRecentlyUsedFilters} = useRecentlyUsedFilters()
  const recentlyUsedFilters = getMappedAllRecentlyUsedFilters({
    limit: RECENTLY_USED_FILTERS_LIMIT,
    neighborhoods,
    propertyBrands
  })

  const filtersChanged = useMemo(
    () => calculateIfFiltersChanged(getFilters()),
    [getFilters, calculateIfFiltersChanged]
  )

  const handleApply = useCallback(() => {
    if (!filtersChanged) {
      dispatch(hideOverlay())
      return
    }
    submitFilters()
  }, [dispatch, submitFilters, filtersChanged])

  const handleClear = useCallback(() => {
    const appliedFiltersCount = getAppliedFiltersCount()
    if (appliedFiltersCount === 0) {
      dispatch(hideOverlay())
      return
    }
    clearFilters()
  }, [dispatch, clearFilters, getAppliedFiltersCount])

  const handleClose = useCallback(() => {
    dispatch(hideOverlay())
    resetFilters()
  }, [dispatch, resetFilters])

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      snapPoints={{top: 'full', middle: 0.98}}
      navBarProps={{
        centerContent: formatMessage(filterMessages.allFilters),
        withDivider: true
      }}
      footerContent={
        <>
          <Divider />
          <Box
            padding="s400"
            paddingLeft="s500"
            paddingRight="s500"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            data-id="allFiltersFilterButtons"
          >
            <ButtonLink dataId="clearFiltersButton" onClick={handleClear}>
              <FormattedMessageWrapper
                id="clearAll"
                defaultMessage="Clear all"
              />
            </ButtonLink>
            <Button dataId="applyFiltersButton" onClick={handleApply}>
              {filtersCount > 0 ? (
                <FormattedMessageWrapper
                  id="filters.applyPlural"
                  defaultMessage="Apply {count, plural, one {1 filter} other {# filters}}"
                  values={{count: filtersCount}}
                />
              ) : (
                <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
              )}
            </Button>
          </Box>
        </>
      }
    >
      <AllFiltersContent
        getFilters={getFilters}
        getAppliedFilters={getAppliedFilters}
        toggleFilters={toggleFilters}
        toggleMultiFilter={toggleMultiFilter}
        getFiltersCount={getFiltersCount}
        setMultiFilters={setMultiFilters}
        removeMultiFilters={removeMultiFilters}
        getAppliedFiltersCount={getAppliedFiltersCount}
        getAppliedStarsLabel={getAppliedStarsLabel}
        recentlyUsedFilters={recentlyUsedFilters}
      />
    </BottomSheet>
  )
}
